import React, { useState, useEffect, useMemo } from "react";
import { PlatformApi, topLoaderProgress } from "../../configs";
import useSWR, { mutate }  from "swr";
import { ApiResponse, WithPagination } from "../../models";
import { AxiosAuth } from "../../core";
import { ApiSchemaCallCenterStatusData } from "../../models/api/CallCenterStatus";
import { ApiSchemaFilter } from "../../models/api/filter";

type Props = {
  callCenterId: string;
  pageSize: number;
  pageNo: number;
  searchText?: string;
  setLoaderProgress: any;
  filterData?: ApiSchemaFilter;
  filterDataChange?: number;
};

let pagedCallCenterStatusUrl: string;


export function useFunctionalityCallCenterStatus(props: Props) {
  const {
    callCenterId,
    pageSize,
    pageNo,
    searchText,
    setLoaderProgress,
    filterData,
    filterDataChange,
  } = props;

  const [statusTableData, setstatusTableData] =
    useState<ApiSchemaCallCenterStatusData[]>();

    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);  

  const pagedCallCenterStatusUrl = useMemo(() => {
    return PlatformApi.CallCenter.Status(
      callCenterId,
      pageSize,
      pageNo,
      searchText,
      filterDataChange
    );
  }, [callCenterId, pageSize, pageNo, searchText, filterDataChange]);

  const { data: apiCallCenterStatusList, error } = useSWR<{
    data: ApiSchemaCallCenterStatusData[];
    count: number;
  }>(
    callCenterId ? pagedCallCenterStatusUrl : null,
    async () => {
      setLoaderProgress(topLoaderProgress.start);
      try {
        const response = await AxiosAuth.post< 
          ApiResponse<WithPagination<ApiSchemaCallCenterStatusData[]>>
        >(pagedCallCenterStatusUrl, filterData);

        setLastUpdated(new Date());
        return {
          data: response.data.data.data,
          count: response.data.data.count,
        };
      } finally {
        setLoaderProgress(topLoaderProgress.complete);
      }
    },
    {
      revalidateOnFocus: false,
      refreshInterval: 10000, // Automatically refresh every 10 seconds
    }
  );

  // Update status table data whenever new data is fetched
  useEffect(() => {
    if (apiCallCenterStatusList?.data) {
      setstatusTableData(apiCallCenterStatusList.data);
    }
  }, [apiCallCenterStatusList]);

    //retrigger a refetch in case it has been sometime (~25 seconds) since last new data has been seen on screen
    useEffect(() => {
      const interval = setInterval(() => {
        if (lastUpdated) {
          const now = new Date().getTime();
          const lastUpdateTime = lastUpdated.getTime();
          if (now - lastUpdateTime > 25000) { 
            console.warn("Forcing SWR revalidation due to inactivity.");
            mutate(pagedCallCenterStatusUrl);
          }
        }
      }, 5000); // Check every 5 seconds
    
      return () => clearInterval(interval);
    }, [pagedCallCenterStatusUrl]);

  // Log and notify in case of an error
  if (error) {
    console.error(error);
  }

  return {
    pagedStatusList: apiCallCenterStatusList?.data ?? statusTableData,
    pagedStatusListCount: apiCallCenterStatusList?.count ?? 0,
    pagedCallCenterStatusUrl,
    lastUpdated,
  };
}


